<template>
  <div class="org-modal" :class="{ showing }">
    <div class="modal-body">
      <i v-show="!saving" class="material-icons close-btn" @click="close()"
        >clear</i
      >

      <div class="top-panel">
        <h4 v-show="!saving">{{ mode }} Organisation</h4>
        <h4 class="saving-header" v-show="saving">Saving Changes</h4>
      </div>

      <div v-show="!saving" class="middle-panel" ref="scrollzone">
        <h6>Organisation details</h6>

        <material-input
          v-model="org.orgName"
          type="text"
          :label="`Organisation name`"
          :required="true"
          name="OrgName"
        />

        <label>Logo</label>
        <input
          type="file"
          accept="image/*"
          ref="file"
          v-on:change="onFileChange()"
        />

        <material-input
          v-model="org.prefix"
          type="text"
          :label="`Prefix`"
          :required="true"
          name="Prefix"
          class="uppercase"
          :maxChars="3"
        />

        <dropdown
          :options="month_options"
          :value="org.fy_month"
          :onChange="val => (org.fy_month = val)"
          placeholder="FY Starts"
        />
        <dropdown
          :options="currencyOptions"
          :value="org.currencyId"
          :onChange="val => (org.currencyId = val)"
          placeholder="Currency"
          :disabled="!!org.id"
        />

        <div v-if="!!org.id">
          <div>
            <label class="mt-4" v-b-toggle="'collapse-conversion-rates'"
              >Exchange Rates</label
            >
            <b-icon-caret-up-fill v-if="conversionRatesShowing" scale="0.6" />
            <b-icon-caret-down-fill v-else scale="0.6" />
          </div>
          <org-conversion-rates
            :org="org"
            :orgId="orgId"
            :orgCurrencyId="org.currencyId"
            @showChanged="val => (conversionRatesShowing = val)"
            @newRate="val => org.conversionRates.push(val)"
          />
        </div>

        <label class="mt-4">Teams</label>
        <div
          v-for="(cat, index) of categories"
          :key="`cat-${index}`"
          class="cat-row"
        >
          <input type="text" v-model="cat.name" />
          <a @click="removeCat(index)"><i class="material-icons">cancel</i></a>
        </div>
        <a @click="addCat()" class="add-btn"
          ><i class="material-icons">add_circle_outline</i>Add</a
        >

        <label class="mt-4">Savings Types</label>
        <div
          v-for="(savingsType, index) of savingsTypes"
          :key="`savingsType-${index}`"
          class="cat-row"
        >
          <input type="text" v-model="savingsType.name" />
          <a @click="removeSavingsType(index)"
            ><i class="material-icons">cancel</i></a
          >
        </div>
        <a @click="addSavingsType()" class="add-btn"
          ><i class="material-icons">add_circle_outline</i>Add</a
        >

        <label class="mt-4">Identity Providers</label>
        <org-identity-provider
          v-for="(organizationIdp, i) of organizationIdps"
          :key="`idProvider-${i}`"
          :idProvider="organizationIdp"
          @change="val => providerChanged(val, i)"
          @delete="removeIdProvider(i)"
        />
        <a @click="addIdProvider()" class="add-btn"
          ><i class="material-icons">add_circle_outline</i>Add</a
        >
      </div>

      <div v-show="saving" class="middle-panel saving">
        <img src="~@/assets/icons/spinner.svg" />
        <p>Saving</p>
      </div>

      <div class="bottom-panel" v-show="!saving">
        <a
          class="button delete-button"
          v-if="orgId && orgId !== -1"
          @click="deleteOrg()"
          >Delete</a
        >
        <a class="button secondary-button" @click="close()">Cancel</a>
        <a class="button" @click="save()" :disabled="formDisabled">Save</a>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialInput from './MaterialInput.vue';
import Dropdown from './Dropdown';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import OrgConversionRates from './OrgConversionRates.vue';
import { BIconCaretDownFill, BIconCaretUpFill } from 'bootstrap-vue';
import OrgIdentityProvider from './OrgIdentityProvider.vue';

export default {
  name: 'OrgModal',
  data() {
    return {
      orgSchema: {
        orgName: '',
        logoUrl: '',
        fy_month: 'April',
        prefix: '',
        currencyId: 1
      },
      saving: false,
      org: {},
      file: '',
      categories: [],
      savingsTypes: [],
      organizationIdps: [],
      month_options: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ].map(m => {
        return { value: m, text: m };
      }),
      jobTitleOptions: [
        'Legal',
        'Finance',
        'Business',
        'Procurement',
        'Other',
      ].map(m => {
        return { value: m, text: m };
      }),
      roleOptions: ['User', 'Admin', 'SuperAdmin'].map(m => {
        return { value: m, text: m };
      }),
      conversionRatesShowing: false,
    };
  },
  props: {
    showing: Boolean,
    mode: String,
    toggleModal: Function,
    onSave: Function,
    onDelete: Function,
    orgId: [Number, String],
  },
  methods: {
    changeTab(index) {
      this.tabIndex = index;
    },
    async onFileChange() {
      try {
        this.file = this.$refs.file.files[0];
      } catch (e) {
        console.log(e);
      }
    },
    async save() {
      this.saving = true;
      try {
        await this.onSave(
          this.org,
          this.file,
          this.categories,
          this.savingsTypes,
          this.organizationIdps
        );
        this.close(false);
      } catch (e) {
        alert(e);
      }
    },
    async deleteOrg() {
      try {
        const options = {
          html: false,
          okText: 'Delete',
          cancelText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Delete organisation',
          body:
            'Are you sure you want to delete this organisation? This action cannot be undone',
        };

        await this.$dialog.confirm(message, options);

        await this.onDelete(this.org, this.categories);
        this.close(false);
      } catch (e) {
        // they clicked cancel, so don't close the modal
      }
    },
    async close() {
      this.reset();
      this.toggleModal(false);
    },
    reset() {
      this.saving = false;
      this.org = { ...this.orgSchema };
      if (this.$refs.file) {
        this.$refs.file.value = null;
      }

      this.file = '';
      this.categories = [];
      this.savingsTypes = [];
      this.organizationIdps = [];
    },
    addCat() {
      this.categories.push({ name: '' });
    },
    removeCat(index) {
      Vue.delete(this.categories, index);
    },
    addSavingsType() {
      this.savingsTypes.push({ name: '' });
    },
    removeSavingsType(index) {
      Vue.delete(this.savingsTypes, index);
    },
    addIdProvider() {
      this.organizationIdps.push({ identityProvider: '', key: '' });
    },
    removeIdProvider(index) {
      Vue.delete(this.organizationIdps, index);
    },
    providerChanged(val, index) {
      this.organizationIdps[index] = val;
      this.$forceUpdate();
    },
  },
  computed: {
    ...mapGetters(['currencies', 'currencyById', 'conversionRates']),
    currencyOptions() {
      return this.currencies.map(currency => ({
        text: currency.code,
        value: currency.id,
      }));
    },
    formDisabled() {
      return !this.org.orgName || !this.org.prefix || !this.org.fy_month;
    },
  },
  watch: {
    orgId(newValue) {
      if (newValue === -1) {
        this.reset();
      } else if (newValue) {
        this.org = this.$store.getters.all_orgs.find(
          org => org.id === newValue
        );
        this.categories = [...this.org.categories];
        this.savingsTypes = [...this.org.savingsTypes];
        this.organizationIdps = [...this.org.organizationIdps];
      }
    },
  },
  components: {
    'material-input': MaterialInput,
    dropdown: Dropdown,
    OrgConversionRates,
    BIconCaretDownFill,
    BIconCaretUpFill,
    OrgIdentityProvider,
  },
  created() {
    this.reset();
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.org-modal {
  opacity: 0;
  visibility: hidden;
  left: -10000px;
  position: absolute;
  z-index: 2000;
  top: 0;
  width: 0;
  height: 0;
  transition: opacity 0.5s;

  .modal-body {
    width: calc(100% - 100px);
    max-width: 740px;
    height: calc(100% - 70px);
    position: fixed;
    z-index: 20;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-50% - 100px)) translateZ(0);
    background: white;
    will-change: transform;
    transition: transform 500ms;
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0;

    .close-btn {
      position: absolute;
      top: 0px;
      right: 0px;
      padding: 10px;
      color: #acb6c1;
      cursor: pointer;
      user-select: none;
      font-size: 20px;
    }

    .top-panel {
      width: 100%;
      height: 126px;
      border-bottom: 1px solid #e2e5ec;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;

      h4 {
        font-size: 24px;
        margin-top: 35px;
      }

      h4.saving-header {
        margin-top: 60px;
      }

      .hidden {
        pointer-events: none;
        visibility: hidden;
      }
    }

    .middle-panel {
      width: 100%;
      flex: 1;
      overflow-x: hidden;
      overflow-y: auto;
      background: $bg-color;
      padding: 30px;
      opacity: 0;
      transition: opacity 0.1s;
      position: relative;

      > input,
      > label {
        max-width: 460px;
        margin: 0 auto 25px;
        font-size: 14px;
        display: block;
        width: 100%;
      }

      > label {
        font-size: 14px;
        margin-bottom: 5px;
      }

      .cat-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 10px auto;
        input {
          height: 40px;
          flex: 1;
          padding: 5px 12px;
          margin-right: 8px;
          font-size: 14px;
        }

        i {
          vertical-align: middle;
          top: -1px;
          position: relative;
          font-size: 20px;
          padding: 3px;
          opacity: 0.3;
          color: $sidebar-color;
          cursor: pointer;

          &:hover {
            opacity: 0.5;
          }
        }
      }

      .add-btn {
        width: 460px;
        margin: 12px auto;
        display: block;
        font-size: 14px;
        opacity: 0.5;
        color: $sidebar-color;
        cursor: pointer;

        i {
          margin-right: 3px;
          vertical-align: middle;
          position: relative;
          top: -1px;
          font-size: 18px;
        }

        &:hover {
          opacity: 0.7;
        }
      }

      > div {
        max-width: 460px;
        display: block;
        margin: 0 auto;
      }

      ::v-deep h6 {
        margin: 20px auto;
        max-width: 460px;

        &:not(:first-child) {
          margin-top: 60px;
        }

        &.spaced {
          margin-top: 40px;
        }
      }

      &.saving {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        flex-wrap: nowrap;

        img {
          width: 40px;
          height: 40px;
          animation: spin 1.5s linear infinite;
        }

        p {
          font-weight: 400;
          font-size: 14px;
          color: #565b64;
          margin: 20px 0 0;
        }
      }
    }

    .bottom-panel {
      width: 100%;
      height: 90px;
      border-top: 1px solid #e2e5ec;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      padding: 0 40px;

      .button {
        width: 130px;
        min-width: 130px;
        margin: 0;
        margin-left: 10px;

        &[disabled] {
          pointer-events: none;
          opacity: 0.5;
        }
      }
    }
  }

  &.showing {
    background: rgba(38, 41, 47, 0.6);
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    visibility: visible;

    .modal-body {
      transform: translateX(-50%) translateY(-50%) translateZ(0);
    }

    .middle-panel {
      opacity: 1;
    }
  }
}

::v-deep .small-caps {
  text-transform: uppercase;
  font-size: 10px;
  color: #929da9;
  font-weight: 500;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
}

::v-deep .grid {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: flex-start;

  > .datepicker {
    width: 215px;
    margin-bottom: 30px;
    margin-top: 0px;

    label {
      text-transform: uppercase;
      font-size: 10px;
      display: block;
      font-weight: 500;
      color: #929da9;
    }
  }
}

::v-deep .uppercase input {
  text-transform: uppercase;
}

.material-input,
.material-select {
  margin-bottom: 30px !important;
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
